// Fresh-eyes 15 (Carla, 2026-10-12): My tasks' two numbers, from the server.
//
// GET /api/tasks/counts -> { dueToday, overdue } (Aidey Backend/lib/taskCounts.js), never
// overlapping. The My tasks subtitle shows both ("3 due today · 17 overdue") and the sidebar badge
// their sum. Asked again shortly after the task list changes (a task added, ticked, moved or
// deleted), on aidey:tasksUpdated, and when the workspace changes. A failed load keeps no number:
// { counts: null, error } with the server's reason.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { messageFromError } from '../utils/apiError';

export default function useTaskCounts(tasks, { enabled = true, workspaceId = null, delayMs = 400 } = {}) {
  const [state, setState] = useState({ counts: null, error: '' });
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const res = await api('/api/tasks/counts');
      const d = await res.json();
      if (mine !== seq.current) return;
      if (typeof d?.dueToday !== 'number' || typeof d?.overdue !== 'number') throw new Error('Your task counts could not be read.');
      setState({ counts: { dueToday: d.dueToday, overdue: d.overdue }, error: '' });
    } catch (err) {
      if (mine !== seq.current) return;
      setState({ counts: null, error: messageFromError(err, 'Your task counts could not be loaded.') });
    }
  }, []);

  // After the list changes, once it has settled (a burst of edits asks once).
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setTimeout(load, delayMs);
    return () => clearTimeout(id);
  }, [enabled, tasks, workspaceId, delayMs, load]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChange = () => { load(); };
    window.addEventListener('aidey:tasksUpdated', onChange);
    return () => window.removeEventListener('aidey:tasksUpdated', onChange);
  }, [enabled, load]);

  return state;
}
